import React, { useLayoutEffect, useRef, useState } from 'react';
import { LuArrowRight, LuCheck, LuCopy, LuShieldCheck, LuBuilding2 } from 'react-icons/lu';
import { getMidiaUrl } from '../services/GerenciarEmpresaService';
import { tintaSobre, razaoDeContraste } from '../utils/contraste';
import '../pages/PaginaCaptura.css';
import './LandingPagePreview.css';

/* Prévia da página de captura, dentro da tela de configuração.

   Ela desenha a página com AS MESMAS CLASSES `cap-` da tela de verdade, num
   aparelho de 390 × 664 — a medida real do alvo. Antes era um desenho paralelo,
   num retângulo largo: mostrava um formulário que a página não tem mais e não
   tinha tela de cupom nenhuma. Prévia que mantém a sua própria versão do layout
   envelhece sozinha e passa a mentir.

   Quando a coluna é estreita demais para os 390px, o aparelho inteiro é
   reduzido por `transform: scale()` — as proporções continuam verdadeiras, só
   menores. Alargando a coluna até 419px (o punho, §8.7 da proposta) a escala
   chega a 1 e a prévia fica em tamanho real. */

const LARGURA = 390;
const ALTURA = 664;

export type AbaPrevia = 'abertura' | 'opcoes' | 'cupom';

/* As três telas em que a configuração aparece: a abertura (chamada e cupom), as
   opções do lojista e o cupom. Os passos 2 e 4 da página não têm nada que ele
   configure, então não viram aba. */
const ABAS: { id: AbaPrevia; rotulo: string }[] = [
  { id: 'abertura', rotulo: 'Abertura' },
  { id: 'opcoes', rotulo: 'Opções' },
  { id: 'cupom', rotulo: 'Cupom' },
];

interface LandingPagePreviewProps {
  primaryColor: string;
  bgColor: string;
  textColor: string;
  logoPath: string | null;
  segmentsList: string[];
  promoActive: boolean;
  promoCode: string;
  promoText: string;
  branchName?: string;
  headline?: string;
  subheadline?: string;
  thanksMessage?: string;
  askSegment?: boolean;
  askEmail?: boolean;
  askCity?: boolean;
}

const LandingPagePreview: React.FC<LandingPagePreviewProps> = React.memo(function LandingPagePreview({
  primaryColor,
  bgColor,
  textColor,
  logoPath,
  segmentsList,
  promoActive,
  promoCode,
  promoText,
  branchName = 'Sua Empresa',
  headline = '',
  subheadline = '',
  thanksMessage = '',
  askSegment = true,
  askEmail = true,
  askCity = true,
}) {
  const [aba, setAba] = useState<AbaPrevia>('abertura');
  const [escala, setEscala] = useState(1);
  const palcoRef = useRef<HTMLDivElement>(null);

  /* A mesma conta de PublicLandingPage.montarPassos: a prévia tem de mostrar o
     número de telas que o cliente vai ver de verdade. */
  const pedeOrigem = askSegment && segmentsList.length > 0;
  const totalDePassos = 2 + (pedeOrigem ? 1 : 0) + (askEmail || askCity ? 1 : 0);

  /* Aba de uma tela que não existe mais é aba que mente: com a pergunta
     desligada, ela sai. */
  const abas = React.useMemo(
    () => ABAS.filter((a) => a.id !== 'opcoes' || pedeOrigem),
    [pedeOrigem],
  );

  /* Se a aba aberta sumiu, volta para a primeira. */
  useLayoutEffect(() => {
    if (!pedeOrigem && aba === 'opcoes') setAba('abertura');
  }, [pedeOrigem, aba]);

  /* A escala vem da largura que a coluna realmente deu — não de um palpite. */
  useLayoutEffect(() => {
    const palco = palcoRef.current;
    if (!palco) return;
    const medir = () => {
      const disponivel = palco.clientWidth;
      if (disponivel > 0) setEscala(Math.min(1, disponivel / LARGURA));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(palco);
    return () => ro.disconnect();
  }, []);

  const primaria = primaryColor || '#0a5c5a';
  const fundo = bgColor || '#0d0f12';
  const contra = razaoDeContraste(primaria, fundo) ?? 0;
  const seguro = tintaSobre(fundo);

  const tema = {
    '--cap-pri': primaria,
    '--cap-bg': fundo,
    '--cap-tx': textColor || '#ffffff',
    '--cap-ink': tintaSobre(primaria),
    '--cap-realce': contra >= 4.5 ? primaria : seguro,
    '--cap-realce-forte': contra >= 3 ? primaria : seguro,
  } as React.CSSProperties;

  const opcoes = segmentsList.length > 0 ? segmentsList : ['Instagram', 'Já conhecia a loja', 'Outros'];
  const inicial = (branchName || '?').trim().charAt(0);

  const topo = (comPasso: false | { atual: number; total: number }) => (
    <>
      <header className="cap-topo">
        {logoPath ? (
          <img src={getMidiaUrl(logoPath)} alt="" className="cap-logo" />
        ) : (
          <div className="cap-logo cap-logo-vazia" aria-hidden="true">
            {inicial || <LuBuilding2 size={18} />}
          </div>
        )}
        <div className="cap-marca">{branchName}</div>
        {comPasso && <div className="cap-passo">{comPasso.atual} de {comPasso.total}</div>}
      </header>
      {comPasso && (
        <div className="cap-prog" aria-hidden="true">
          {Array.from({ length: comPasso.total }, (_, i) => (
            <i key={i} className={i < comPasso.atual ? 'on' : undefined} />
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="lpp">
      <div className="lpp-abas" role="tablist" aria-label="Telas da página de captura">
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={aba === a.id}
            className={`lpp-aba${aba === a.id ? ' on' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div className="lpp-palco" ref={palcoRef}>
        <div
          className="lpp-moldura"
          style={{
            width: LARGURA * escala,
            height: ALTURA * escala,
          }}
        >
          <div
            className="lpp-aparelho"
            style={{
              width: LARGURA,
              height: ALTURA,
              transform: `scale(${escala})`,
            }}
          >
            <div className="cap-raiz" style={tema}>
              {aba === 'abertura' && (
                <>
                  {topo({ atual: 1, total: totalDePassos })}
                  <div className="cap-corpo">
                    <div className="cap-promessa">
                      <p className="cap-pr-t">
                        {headline || (promoActive && promoText ? <>Ganhe <u>{promoText}</u></> : <>Cadastre-se na {branchName}</>)}
                      </p>
                      <p className="cap-pr-s">{subheadline || 'Leva 30 segundos. Só o nome e o WhatsApp são obrigatórios.'}</p>
                    </div>
                    <p className="cap-q">Como você se chama?</p>
                    <p className="cap-aj">É o nome que vai no cupom.</p>
                    <div className="cap-campo lpp-falso">Seu nome</div>
                  </div>
                  <div className="cap-barra">
                    <div className="cap-btn">Continuar <LuArrowRight size={17} /></div>
                  </div>
                </>
              )}

              {aba === 'opcoes' && (
                <>
                  {topo({ atual: 3, total: totalDePassos })}
                  <div className="cap-corpo cap-rola">
                    <p className="cap-q">Como você conheceu a gente?</p>
                    <div className="cap-ops">
                      {opcoes.map((op, i) => (
                        <div key={op + i} className={`cap-op${i === 1 ? ' on' : ''}`}>
                          <span>{op}</span>
                          {i === 1 && <LuCheck className="cap-mk" size={17} />}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cap-barra">
                    <div className="cap-btn">Continuar <LuArrowRight size={17} /></div>
                  </div>
                </>
              )}

              {aba === 'cupom' && (
                <>
                  {topo(false)}
                  <div className="cap-corpo">
                    <p className="cap-q">Pronto, Maria.</p>
                    <p className="cap-aj">
                      {promoActive && promoCode
                        ? 'Mostre este código no caixa.'
                        : (thanksMessage || 'Seu cadastro foi registrado. Obrigado!')}
                    </p>
                    {promoActive && promoCode && (
                      <>
                        <div className="cap-cod">
                          <div className="cap-cod-lb">Seu cupom</div>
                          <div className="cap-cod-v">{promoCode}</div>
                          {promoText && <p className="cap-cod-tx">{promoText}</p>}
                        </div>
                        <div className="cap-copiar"><LuCopy size={16} /><span>Copiar código</span></div>
                        <p className="cap-rod">
                          <LuShieldCheck size={14} />
                          <span>Este código fica guardado neste link. Pode fechar e voltar depois.</span>
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="lpp-regua">
        {escala >= 1
          ? 'Tamanho real — 390 × 664, como no celular.'
          : `${Math.round(escala * 100)}% do tamanho real. Arraste a divisória para ampliar.`}
      </p>
    </div>
  );
});

export default LandingPagePreview;
